import { useEffect, useMemo, useState, type ReactNode } from 'react'
import { supabase } from '@/lib/supabase'
import { useAuth } from '@/lib/auth'
import { AdminContext } from '@/lib/admin'

/**
 * Looks up user_profiles.is_super_admin once per auth-state transition and
 * caches the result in context. SiteHeader and AdminGuard read from here
 * instead of issuing a query on every render.
 *
 * Must sit inside <AuthProvider>.
 */
export function AdminProvider({ children }: { children: ReactNode }) {
  const { user, loading: authLoading } = useAuth()
  const [isAdmin, setIsAdmin] = useState(false)
  const [loading, setLoading] = useState(true)

  useEffect(() => {
    // Wait for auth bootstrap before deciding.
    if (authLoading) return

    let cancelled = false

    // Signed-out users are definitively not admins. Defer the state writes
    // to the next microtask to avoid the "setState during effect" warning
    // (this is the same pattern other providers in the repo use).
    if (!user) {
      queueMicrotask(() => {
        if (cancelled) return
        setIsAdmin(false)
        setLoading(false)
      })
      return () => {
        cancelled = true
      }
    }

    queueMicrotask(() => {
      if (cancelled) return
      setLoading(true)
    })
    void supabase
      .from('user_profiles')
      .select('is_super_admin')
      .eq('user_id', user.id)
      .maybeSingle()
      .then(({ data, error }) => {
        if (cancelled) return
        setIsAdmin(!error && data?.is_super_admin === true)
        setLoading(false)
      })

    return () => {
      cancelled = true
    }
  }, [user, authLoading])

  const value = useMemo(() => ({ isAdmin, loading }), [isAdmin, loading])
  return <AdminContext.Provider value={value}>{children}</AdminContext.Provider>
}
